import { Utils } from '@livecontrol/core-ui';
import type { DateTime } from 'luxon';
import type { ReactElement } from 'react';
import styled, { css } from 'styled-components';
import { Store } from '../../store';
import { useMyEventsStats } from './graphql';
import { myEventsCutoff } from './my-events-cutoff';
import { useEventsStore } from './store';

const Bar = styled.div`
  display: flex;
  align-items: stretch;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const StatCard = styled.div`
  width: 130px;
  padding: 13px 15px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 13px;
  background: rgba(17, 27, 51, 0.4);
  display: flex;
  flex-direction: column;
  gap: 9px;

  .head {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.55);
    font-weight: 700;
    font-size: 9px;
  }

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgb(53, 211, 153);
  }

  .info {
    font-size: 11px;
    color: rgba(176, 186, 209, 0.55);
  }

  .count {
    color: rgb(240, 242, 245);
    font-weight: 800;
    font-size: 30px;
    line-height: 1;
  }
`;

const UpcomingCard = styled(StatCard)`
  position: relative;

  &:hover .tooltip {
    opacity: 1;
    pointer-events: auto;
  }
`;

// Shared tooltip shell used by both the Upcoming and Next Event popovers.
const tipBase = css`
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  width: 288px;
  padding: 14px 15px;
  border-radius: 12px;
  background: rgb(13, 23, 51);
  border: 1px solid rgba(120, 130, 255, 0.3);
  box-shadow: rgba(0, 0, 0, 0.5) 0px 18px 44px;
  z-index: 40;
  opacity: 0;
  pointer-events: none;
  text-align: left;
  transition: opacity 0.14s;

  /* Bridge the 10px gap so the tooltip stays open while moving into it. */
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: 0;
    right: 0;
    height: 10px;
  }
`;

const Tip = styled.div`
  ${tipBase}

  .tip-title {
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.55);
    margin-bottom: 11px;
    font-weight: 700;
    font-size: 9px;
  }

  .tip-empty {
    font-size: 12px;
    color: rgb(138, 152, 181);
  }

  .tip-list {
    max-height: 212px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .entry {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 9px 8px;
    border-radius: 9px;
  }

  .tip-row {
    display: flex;
    align-items: baseline;
    gap: 7px;
  }

  .e-title {
    flex: 1 1 0%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: rgb(240, 242, 245);
    font-weight: 700;
    font-size: 12px;
  }

  .e-org {
    flex: 1 1 0%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: rgba(205, 214, 234, 0.75);
    font-weight: 600;
    font-size: 11px;
  }

  .mono {
    flex-shrink: 0;
    color: rgba(176, 186, 209, 0.6);
    font-weight: 600;
    font-size: 9px;
    font-family: 'JetBrains Mono', monospace;
  }

  .golive {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 700;
    font-size: 11px;
    color: rgb(159, 176, 255);
  }

  .late {
    border-radius: 999px;
    padding: 2px 6px;
    background: rgba(242, 179, 61, 0.16);
    color: rgb(255, 210, 122);
    font-weight: 700;
    font-size: 8px;
    letter-spacing: 0.05em;
  }
`;

const NextTip = styled.div`
  ${tipBase}
  display: flex;
  flex-direction: column;
  gap: 10px;

  .n-entry {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .n-entry.last {
    padding-top: 9px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }

  .n-label {
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.55);
    font-weight: 700;
    font-size: 9px;
  }

  .n-value {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: rgb(240, 242, 245);
    font-weight: 700;
    font-size: 14px;
  }

  .n-mono {
    color: rgba(176, 186, 209, 0.6);
    font-weight: 600;
    font-size: 10px;
    font-family: 'JetBrains Mono', monospace;
  }

  .n-time {
    color: rgb(159, 176, 255);
    font-weight: 700;
    font-size: 12px;
  }
`;

const NextCard = styled.div`
  position: relative;
  width: 440px;
  max-width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 13px 18px;
  border: 1px solid rgba(120, 130, 255, 0.28);
  border-radius: 13px;
  background: linear-gradient(135deg, rgba(78, 95, 253, 0.15), rgba(139, 92, 246, 0.06));
  cursor: default;

  &:hover .next-tooltip {
    opacity: 1;
    pointer-events: auto;
  }

  .left {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }

  .kicker {
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.6);
    font-weight: 700;
    font-size: 9px;
  }

  .line {
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }

  .title {
    color: rgb(255, 255, 255);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 800;
    font-size: 17px;
  }

  .org {
    color: rgba(205, 214, 234, 0.75);
    font-weight: 600;
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mono {
    color: rgba(176, 186, 209, 0.6);
    flex-shrink: 0;
    font-weight: 600;
    font-size: 10px;
    font-family: 'JetBrains Mono', monospace;
  }

  .right {
    flex-shrink: 0;
    text-align: right;
  }

  .goes {
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.6);
    margin-bottom: 6px;
    font-weight: 700;
    font-size: 8px;
  }

  .timer {
    font-weight: 700;
    font-size: 29px;
    line-height: 1;
    color: rgb(159, 176, 255);
    letter-spacing: 0.01em;
    font-family: 'JetBrains Mono', monospace;
  }
`;

const TITLE_MAX = 20;
// Upcoming tooltip has more room, so allow a bit more of the title before truncating.
const TIP_TITLE_MAX = TITLE_MAX + 10;
const LATE_HOUR = 4;

const truncate = (text: string, max: number): string =>
  text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;

const pad = (n: number): string => String(Math.max(0, n)).padStart(2, '0');

// "Goes live" time in the viewer's timezone, plus whether it's a late-night (after midnight) slot.
const formatGoLive = (start: DateTime): { time: string; late: boolean } => {
  const local = start.toLocal();

  return {
    time: `${local.toFormat('h:mm a')} ${local.toFormat('ZZZZ')}`,
    late: local.hour < LATE_HOUR
  };
};

// Single-line countdown until the event goes live (H:MM:SS, with a day prefix if needed).
const GoesLiveIn = ({ start }: { start: DateTime }): ReactElement => {
  const { days, hours, minutes, seconds } = Utils.useCountdown(start);

  const text =
    days > 0
      ? `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
      : `${hours}:${pad(minutes)}:${pad(seconds)}`;

  return <span className='timer'>{text}</span>;
};

export const MyEventsStats = (): ReactElement => {
  const { me } = Store.User.useIdentity();
  const verifierEmail = useEventsStore((v) => v.filters.verifierEmail);

  const selectedEmail = verifierEmail ?? me?.email;

  // Favorites count toward the stats only when the validator is the logged-in user.
  const viewingSelf =
    selectedEmail !== undefined && selectedEmail.toLowerCase() === (me?.email ?? '').toLowerCase();

  const { liveCount, upcomingCount, nextEvent, upcomingEvents } = useMyEventsStats(
    selectedEmail,
    viewingSelf
  );

  // Events going live between now and the upcoming 4 AM boundary (the operational "today").
  const cutoff = myEventsCutoff();
  const tooltipEvents = upcomingEvents.filter((event) => event.start.toLocal() <= cutoff);

  return (
    <Bar>
      <StatCard>
        <span className='head'>
          <span className='dot cc-blink-slow' />
          Live Now
        </span>
        <span className='count'>{liveCount}</span>
      </StatCard>

      <UpcomingCard>
        <span className='head'>
          Upcoming
          <i className='info far fa-info-circle' />
        </span>
        <span className='count'>{upcomingCount}</span>

        <Tip className='tooltip'>
          <div className='tip-title'>Your upcoming today · through 4 AM</div>

          {tooltipEvents.length === 0 ? (
            <div className='tip-empty'>Nothing upcoming before 4 AM.</div>
          ) : (
            <div className='tip-list cc-scroll'>
              {tooltipEvents.map((event) => {
                const { time, late } = formatGoLive(event.start);

                return (
                  <div className='entry' key={event.eid}>
                    <div className='tip-row'>
                      <span className='e-title'>{truncate(event.title, TIP_TITLE_MAX)}</span>
                      <span className='mono'>EID {event.eid}</span>
                    </div>

                    <div className='tip-row'>
                      {event.organizationName !== undefined && (
                        <span className='e-org'>{event.organizationName}</span>
                      )}
                      <span className='mono'>CID {event.cid}</span>
                    </div>

                    <div className='golive'>
                      <i className='far fa-clock' />
                      Goes live {time}
                      {late && <span className='late'>LATE</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Tip>
      </UpcomingCard>

      <NextCard>
        <div className='left'>
          <span className='kicker'>Next Event Live</span>

          {nextEvent ? (
            <>
              <span className='line'>
                <span className='title'>{truncate(nextEvent.title, TITLE_MAX)}</span>
                <span className='mono'>EID {nextEvent.eid}</span>
              </span>

              <span className='line'>
                {nextEvent.organizationName !== undefined && (
                  <span className='org'>{nextEvent.organizationName}</span>
                )}
                <span className='mono'>CID {nextEvent.cid}</span>
              </span>
            </>
          ) : (
            <span className='org'>No upcoming events assigned to you.</span>
          )}
        </div>

        {nextEvent && (
          <div className='right'>
            <div className='goes'>Goes Live in</div>
            <GoesLiveIn start={nextEvent.start} />
          </div>
        )}

        {nextEvent && (
          <NextTip className='next-tooltip'>
            <div className='n-entry'>
              <div className='n-label'>Event</div>
              <div className='n-value'>{nextEvent.title}</div>
              <div className='n-mono'>EID {nextEvent.eid}</div>
            </div>

            <div className='n-entry'>
              <div className='n-label'>Company</div>
              <div className='n-value'>{nextEvent.organizationName ?? '—'}</div>
              <div className='n-mono'>CID {nextEvent.cid}</div>
            </div>

            <div className='n-entry last'>
              <div className='n-label'>Goes live · your time</div>
              <div className='n-time'>
                {formatGoLive(nextEvent.start).time} ·{' '}
                {nextEvent.start.toLocal().toFormat('ccc, LLL d')}
              </div>
            </div>
          </NextTip>
        )}
      </NextCard>
    </Bar>
  );
};
